// DE profile, a DE set up from a role (TEMPLATES-T4, design §3 step 6): its probation and its
// access per connection family. While on probation it works at Draft level (every outward action
// at most With approval) until its tasks and days are met; its supervisor or an owner ends it.
// The access rows (level, where it came from) change in a modal: tightening is free, a level above
// the role's default needs "Confirm wider access" (owner or supervisor; the API checks), and
// nothing goes above an overlay's cap. Nothing shows for a DE not set up from a role.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InfoTip,
  Label,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, ShieldCheckIcon } from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import type { CompanyAccess } from "../company";
import { keys } from "../queries";
import { LoadError } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { DENSE_TABLE, RowAction } from "../settings/row-actions";
import {
  type AccessFamily,
  type AccessLevel,
  type DeAccess,
  deAccessQuery,
  FAMILY_LABELS,
  LEVEL_LABELS,
  levelsUpTo,
  levelText,
  SOURCE_LABELS,
  templateKeys,
  widens,
} from "../templates/role-model";

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const DRAFT_TIP =
  "Draft level: every outward action waits for approval until probation ends. Its supervisor or an owner ends it once the tasks and days are met.";

/** On probation now (started, not ended), from the profile's own columns. */
export const onProbation = (
  de: Pick<DeProfile, "probation_started_at" | "probation_ended_at">,
): boolean => de.probation_started_at !== null && de.probation_ended_at === null;

/**
 * The header's small "Probation · 10 tasks · until Nov 12" badge, read from the profile (no extra
 * call; the live count is the access section's, the profile only stores it once probation ends);
 * nothing for a DE not on probation.
 */
export function DeProbationBadge({ de }: { de: DeProfile }) {
  if (!onProbation(de)) return null;
  return (
    <span className="inline-flex items-center gap-0.5" data-slot="de-probation">
      <Badge variant="warning">
        Probation · {de.probation_tasks_required ?? 0} tasks
        {de.probation_until ? ` · until ${shortDate(de.probation_until)}` : ""}
      </Badge>
      <InfoTip label="About probation">{DRAFT_TIP}</InfoTip>
    </span>
  );
}

/** Setup › Permissions: the role's probation and access (hidden for a DE with neither). */
export function DeRoleAccess({
  api,
  company,
  deId,
  name,
}: {
  api: Api;
  company: CompanyAccess;
  deId: string;
  name: string;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const access = useQuery(deAccessQuery(api, co, deId));
  const [editing, setEditing] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const end = useMutation({
    mutationFn: () => api.client.digitalEmployees.endProbation({ params: { co, id: deId } }),
    onSuccess: () => toast.add({ title: `${name}'s probation ended`, type: "success" }),
    onSettled: () => qc.invalidateQueries({ queryKey: keys.de(co, deId) }),
  });
  if (access.isPending) return null;
  if (access.isError) return <LoadError what="Access" error={access.error} />;
  const v = access.data;
  if (!v.data.length && !v.probation) return null;
  const p = v.probation;
  return (
    <section className="flex min-w-0 flex-col gap-3" data-slot="de-role-access" aria-label="Access">
      <h2 className="flex items-center gap-2 text-sm font-medium">
        <ShieldCheckIcon className="size-4 text-muted-foreground" aria-hidden="true" />
        Access
      </h2>
      {p ? (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-slot="de-probation-row">
          {p.ended_at === null ? (
            <>
              <Badge variant="warning">Probation</Badge>
              <span>
                {p.tasks_done}/{p.tasks_required} tasks · until {shortDate(p.until)}
              </span>
              <InfoTip label="About probation">{DRAFT_TIP}</InfoTip>
              <span className="text-xs text-muted-foreground">
                {p.record.approved_as_is} approved · {p.record.edited} edited · {p.record.rejected}{" "}
                rejected
              </span>
              <Button
                size="sm"
                variant="secondary"
                className="ml-auto"
                disabled={end.isPending}
                onClick={() => setConfirmEnd(true)}
              >
                End probation
              </Button>
            </>
          ) : (
            <span className="text-muted-foreground">
              Probation ended {p.ended_at ? shortDate(p.ended_at) : ""}
            </span>
          )}
        </div>
      ) : null}
      {end.isError ? (
        <Notice tone="danger" role="alert" data-slot="end-probation-error">
          {problemText(end.error, "Probation was not ended.")}
        </Notice>
      ) : null}
      {v.data.length ? (
        <div className="group/row flex flex-col gap-1">
          <div className="flex items-center justify-end">
            <RowAction
              icon={PencilIcon}
              label={`Change ${name}'s access`}
              onClick={() => setEditing(true)}
            />
          </div>
          <Table className={DENSE_TABLE} data-slot="de-access-rows">
            <TableHeader>
              <TableRow>
                <TableHead>Family</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Source</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {v.data.map((r) => (
                <TableRow key={r.family} data-family={r.family}>
                  <TableCell>{FAMILY_LABELS[r.family]}</TableCell>
                  <TableCell>{LEVEL_LABELS[r.level]}</TableCell>
                  <TableCell>{levelText(r.role_level)}</TableCell>
                  <TableCell>{SOURCE_LABELS[r.source]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
      <AlertDialog open={confirmEnd} onOpenChange={setConfirmEnd}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End {name}'s probation?</AlertDialogTitle>
            <AlertDialogDescription>Its access applies in full from now on.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmEnd(false);
                end.mutate();
              }}
            >
              End probation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {editing ? (
        <AccessDialog
          api={api}
          co={co}
          deId={deId}
          name={name}
          view={v}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </section>
  );
}

function AccessDialog({
  api,
  co,
  deId,
  name,
  view,
  onClose,
}: {
  api: Api;
  co: string;
  deId: string;
  name: string;
  view: DeAccess;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [levels, setLevels] = useState<Partial<Record<AccessFamily, AccessLevel>>>(() =>
    Object.fromEntries(view.data.map((r) => [r.family, r.level])),
  );
  const [confirm, setConfirm] = useState<AccessFamily[]>([]);
  const changed = view.data.filter((r) => levels[r.family] !== r.level);
  const wider = changed.filter((r) => widens(levels[r.family], r.role_level));
  const save = useMutation({
    mutationFn: () =>
      api.client.digitalEmployees.putAccess({
        params: { co, id: deId },
        body: {
          access: Object.fromEntries(changed.map((r) => [r.family, levels[r.family]])),
          ...(confirm.length
            ? { confirm_widen: confirm.filter((f) => wider.some((r) => r.family === f)) }
            : {}),
        },
      }),
    onSuccess: (next) => {
      qc.setQueryData(templateKeys.deAccess(co, deId), next);
      void qc.invalidateQueries({ queryKey: keys.de(co, deId) });
      toast.add({ title: `${name}'s access saved`, type: "success" });
      onClose();
    },
  });
  const ready = changed.length > 0 && wider.every((r) => confirm.includes(r.family));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <form onSubmit={submit} className="flex flex-col gap-4" data-slot="de-access-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1">
              {name}'s access
              <InfoTip label="About access changes">
                Tighten freely. Above the role's level needs an owner or its supervisor; never above
                the overlay's cap.
              </InfoTip>
            </DialogTitle>
          </DialogHeader>
          <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
            {view.data.map((r) => {
              const value = levels[r.family] ?? r.level;
              const widened = widens(value, r.role_level) && value !== r.level;
              return (
                <li
                  key={r.family}
                  data-family={r.family}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
                >
                  <span className="min-w-24 font-medium">{FAMILY_LABELS[r.family]}</span>
                  <span className="text-xs text-muted-foreground">
                    Role: {levelText(r.role_level)}
                  </span>
                  <NativeSelect
                    aria-label={`${FAMILY_LABELS[r.family]} level`}
                    className="ml-auto h-8 w-36"
                    value={value}
                    onChange={(e) =>
                      setLevels((l) => ({ ...l, [r.family]: e.target.value as AccessLevel }))
                    }
                  >
                    {levelsUpTo(view.caps[r.family]).map((l) => (
                      <NativeSelectOption key={l} value={l}>
                        {LEVEL_LABELS[l]}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                  {widened ? (
                    <span className="flex w-full items-center justify-end gap-1.5">
                      <Checkbox
                        id={`de-widen-${r.family}`}
                        checked={confirm.includes(r.family)}
                        onCheckedChange={(v) =>
                          setConfirm((c) =>
                            v === true ? [...c, r.family] : c.filter((x) => x !== r.family),
                          )
                        }
                      />
                      <Label htmlFor={`de-widen-${r.family}`} className="text-xs">
                        Confirm wider access
                      </Label>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {save.isError ? (
            <Notice tone="danger" role="alert" data-slot="de-access-error">
              {problemText(save.error, "The access was not saved.")}
            </Notice>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready || save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
